import React from 'react';
import {
    MapPin,
    Edit,
    Navigation,
    Plus,
    Repeat,
    CalendarDays,
    Tag,
    ChevronDown,
    ArrowLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import PinnedNotesDisplay from '../wells/PinnedNotesDisplay'; // Fixed import to be named export if possible, otherwise default
import { WellTagBadges } from '../WellTags';
import { cn } from '../ui/utils';
import { toast } from 'sonner';

interface WellHeaderProps {
    well: any;
    wells: any[];
    onBack: () => void;
    onEdit: () => void;
    onNavigate: () => void;
    onDataEntry: () => void;
    onAutoSaveEntry?: () => void;
    onWellChange?: (well: any) => void;
    onOpenDatePicker: () => void;
    onOpenTags: () => void;
    isDarkMode: boolean;
}

export function WellHeader({
    well,
    wells,
    onBack,
    onEdit,
    onNavigate,
    onDataEntry,
    onAutoSaveEntry,
    onWellChange,
    onOpenDatePicker,
    onOpenTags,
    isDarkMode
}: WellHeaderProps) {

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>;
            case 'maintenance': return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Maintenance</Badge>;
            case 'inactive': return <Badge className="bg-slate-100 text-slate-800 border-slate-200">Inactive</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    return (
        <div className={cn(
            "border-b sticky top-0 z-30 transition-colors duration-200",
            isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"
        )}>
            {/* Top Bar with Back Button */}
            <div className="px-4 py-3 flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={onBack} className="-ml-2">
                    <ArrowLeft className={cn("w-5 h-5", isDarkMode ? "text-slate-400" : "text-slate-600")} />
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className={cn("text-lg font-bold truncate", isDarkMode ? "text-white" : "text-slate-900")}>
                            {well.name}
                        </h2>
                        {getStatusBadge(well.status || 'active')}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="truncate">{well.location || 'No location set'}</span>
                    </div>
                </div>

                {/* Quick Actions (Desktop/Tablet mostly) */}
                <div className="hidden sm:flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={onNavigate} className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/40">
                        <Navigation className="w-4 h-4 mr-2" /> Navigate
                    </Button>
                    <Button variant="outline" size="sm" onClick={onEdit}>
                        <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    {onAutoSaveEntry ? (
                        <Button size="sm" onClick={onAutoSaveEntry} className="bg-blue-600 hover:bg-blue-700 text-white">
                            <Plus className="w-4 h-4 mr-2" /> Entry
                        </Button>
                    ) : (
                        <Button size="sm" onClick={onDataEntry} className="bg-blue-600 hover:bg-blue-700 text-white">
                            <Plus className="w-4 h-4 mr-2" /> Add Entry
                        </Button>
                    )}
                </div>
                {/* Mobile Add Button (Icon only) */}
                <Button size="icon" className="sm:hidden bg-blue-600 hover:bg-blue-700 text-white rounded-full h-9 w-9" onClick={onAutoSaveEntry || onDataEntry}>
                    <Plus className="w-5 h-5" />
                </Button>
            </div>

            {/* Expanded Controls & Context */}
            <div className="px-4 pb-4 space-y-4">
                {/* Pinned Notes */}
                {well.pinnedNotes && well.pinnedNotes.length > 0 && (
                    <div className="animate-in fade-in slide-in-from-top-2">
                        {/* Assuming PinnedNotesDisplay handles its own rendering gracefully */}
                        {/* We use 'any' cast if types aren't perfectly aligned during this refactor transition */}
                        <PinnedNotesDisplay
                            notes={well.pinnedNotes}
                            wellName={well.name}
                            compact={true}
                        />
                    </div>
                )}

                {/* Well Selector */}
                {wells && wells.length > 0 && onWellChange && (
                    <div className="relative group">
                        <div className={cn(
                            "absolute inset-y-0 left-3 flex items-center pointer-events-none",
                            isDarkMode ? "text-slate-500" : "text-slate-400"
                        )}>
                            <Repeat className="w-4 h-4" />
                        </div>
                        <select
                            value={well.id}
                            onChange={(e) => {
                                const selected = wells.find(w => w.id === e.target.value);
                                if (selected) {
                                    onWellChange(selected);
                                    toast.success(`Switched to ${selected.name}`);
                                }
                            }}
                            className={cn(
                                "w-full pl-10 pr-10 py-2.5 rounded-xl border text-sm font-medium appearance-none transition-shadow focus:ring-2 focus:ring-blue-500/20 focus:outline-none cursor-pointer",
                                isDarkMode
                                    ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-750"
                                    : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                            )}
                        >
                            {wells.map(w => (
                                <option key={w.id} value={w.id} className={isDarkMode ? "bg-slate-800" : "bg-white"}>
                                    {w.name} • {w.location}
                                </option>
                            ))}
                        </select>
                        <ChevronDown className={cn(
                            "absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none",
                            isDarkMode ? "text-slate-500" : "text-slate-400"
                        )} />
                    </div>
                )}

                {/* Secondary Actions Row */}
                <div className="grid grid-cols-4 gap-3">
                    {/* Navigation Button */}
                    <Button
                        onClick={onNavigate}
                        className={cn(
                            "h-11 shadow-sm",
                            isDarkMode
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                        )}
                    >
                        <Navigation className="w-5 h-5 mr-2" />
                        Navigate Here
                    </Button>
                    {/* Add Entry Button */}
                    <Button
                        onClick={onAutoSaveEntry ? onAutoSaveEntry : onDataEntry}
                        className={cn(
                            "h-11 shadow-sm",
                            isDarkMode
                                ? "bg-blue-600 hover:bg-blue-700 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                        )}
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Entry
                    </Button>
                    {/* Date Picker Button */}
                    <Button
                        variant="outline"
                        onClick={onOpenDatePicker}
                        className={cn(
                            "justify-start h-10",
                            isDarkMode ? "bg-slate-800/50 border-slate-700 hover:bg-slate-800" : "bg-white border-slate-200 hover:bg-slate-50"
                        )}
                    >
                        <CalendarDays className="w-4 h-4 mr-2 text-blue-500" />
                        <span className="text-xs">Jump to Date</span>
                    </Button>
                    {/* Tags Button */}
                    <Button
                        variant="outline"
                        onClick={onOpenTags}
                        className={cn(
                            "justify-start h-10 relative",
                            isDarkMode ? "bg-slate-800/50 border-slate-700 hover:bg-slate-800" : "bg-white border-slate-200 hover:bg-slate-50"
                        )}
                    >
                        <Tag className="w-4 h-4 mr-2 text-purple-500" />
                        <span className="text-xs">Tags</span>
                        {(well.tags?.length || 0) > 0 && (
                            <Badge variant="default" className="ml-auto bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200 h-5 px-1.5 text-[10px]">
                                {well.tags.length}
                            </Badge>
                        )}
                    </Button>
                </div>

                {/* Tags Display */}
                {well.tags && well.tags.length > 0 && (
                    <div className="pt-1">
                        <WellTagBadges tags={well.tags} />
                    </div>
                )}
            </div>
        </div>
    );
}
